import { useCallback, useMemo } from 'react'

import styled from 'styled-components'

import Text, { TextSize } from 'src/components/Typography/Text'

import Checkbox from '../Checkbox/Checkbox'
import VStack from '../Flex/VStack'

import SelectMenuItem from './SelectMenuItem'
import SelectMenuItemGroupLabel from './SelectMenuItemGroupLabel'
import { SelectOption } from './types'

const StyledSelectMenuItem = styled(SelectMenuItem)`
  padding: 6px 9px;
`

type Props<TValue> = {
  id: string
  index: number
  isActive: boolean
  isDisabled: boolean
  isSelected: boolean
  menuItemRefs: React.MutableRefObject<Map<number, HTMLElement | null>>
  option: SelectOption<TValue>
  options: SelectOption<TValue>[]
  onMouseOver: (index: number) => void
  onClick: (option: SelectOption<TValue>) => void
  isHideCheckbox?: boolean
}
const MultiSelectItem = <TValue,>({
  id,
  index,
  isActive,
  isDisabled,
  isSelected,
  menuItemRefs,
  option,
  options,
  onMouseOver,
  onClick,
  isHideCheckbox,
}: Props<TValue>) => {
  const handleMouseOver = useCallback(() => {
    onMouseOver(index)
  }, [index, onMouseOver])
  const handleClick = useCallback(() => {
    onClick(option)
  }, [onClick, option])

  const selectMenuContent: React.ReactNode = useMemo(() => {
    {
      return option.customDisplay ? (
        option.customDisplay(option.value)
      ) : (
        <VStack gap={1}>
          <Text
            strong={isActive}
            lineHeight="0"
            {...(isDisabled ? { colorToken: 'colorTextTertiary' } : { color: 'inherit' })}
          >
            {option.label}
          </Text>

          {option.sublabel && (
            <Text
              colorToken={isActive ? 'colorPrimaryBase' : 'colorTextSecondary'}
              size={TextSize.Small}
              strong={isSelected}
            >
              {option.sublabel}
            </Text>
          )}
        </VStack>
      )
    }
  }, [isActive, isDisabled, isSelected, option])

  return (
    <>
      <SelectMenuItemGroupLabel index={index} option={option} options={options} />
      <StyledSelectMenuItem
        id={id}
        onMouseOver={handleMouseOver}
        onClick={handleClick}
        $isDisabled={isDisabled}
        ref={(el) => menuItemRefs.current.set(index, el)}
        {...(isActive
          ? {
              ['data-selected']: true, // controls option remaining highlighted when submenu is expanded
              ['aria-selected']: true,
            }
          : {})}
      >
        <div className="pointer-events-none p-1">
          {!isHideCheckbox ? (
            <Checkbox value={isSelected} tabIndex={-1}>
              {selectMenuContent}
            </Checkbox>
          ) : (
            <>{selectMenuContent}</>
          )}
        </div>
      </StyledSelectMenuItem>
    </>
  )
}

export default React.memo(MultiSelectItem) as typeof MultiSelectItem
